import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Plus, Minus, ShoppingCart, Clock, Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { api, errMsg, fileUrl, fmtUsdt } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Price, Section, Empty } from "@/components/common";
import { PaymentOptions, canPay } from "@/components/DriverPicker";

export default function Food() {
  const { user, settings, refresh } = useAuth();
  const navigate = useNavigate();
  const [items, setItems] = useState([]);
  const [cat, setCat] = useState("Todas");
  const [cart, setCart] = useState({});
  const [open, setOpen] = useState(false);
  const [address, setAddress] = useState(user?.address || "");
  const [notes, setNotes] = useState("");
  const [busy, setBusy] = useState(false);
  const [currency, setCurrency] = useState("USDT");
  const [driverId, setDriverId] = useState(null);
  const [driverFee, setDriverFee] = useState(null);
  const [point, setPoint] = useState(null);
  const pickDriver = (id, d) => { setDriverId(id); setDriverFee(d?.driver_fee || null); };
  const inHours = (() => { const s = settings.order_hours_start, e = settings.order_hours_end; if (!s || !e) return true; const now = new Date().toLocaleTimeString("en-GB", { timeZone: "America/Havana", hour: "2-digit", minute: "2-digit" }); return s <= e ? now >= s && now <= e : now >= s || now <= e; })();

  useEffect(() => { api.get("/food").then((r) => setItems(r.data.filter((i) => i.available))).catch(() => {}); }, []);
  const cats = useMemo(() => ["Todas", ...new Set(items.map((i) => i.category))], [items]);
  const shown = items.filter((i) => cat === "Todas" || i.category === cat);
  const lines = Object.values(cart);
  const subtotal = lines.reduce((s, l) => s + (l.item.offer_price || l.item.price) * l.qty, 0);
  const total = subtotal + (lines.length ? (driverFee ?? settings.delivery_fee) : 0);
  const count = lines.reduce((s, l) => s + l.qty, 0);

  const add = (item, d = 1) => setCart((c) => {
    const qty = (c[item.id]?.qty || 0) + d;
    const n = { ...c };
    if (qty <= 0) delete n[item.id]; else n[item.id] = { item, qty };
    return n;
  });

  const checkout = async () => {
    setBusy(true);
    try {
      await api.post("/orders", { type: "food", items: lines.map((l) => ({ id: l.item.id, qty: l.qty })), address, notes, pay_currency: currency, driver_id: driverId, address_lat: point?.lat, address_lng: point?.lng });
      toast.success("¡Pedido realizado! Saldo retenido hasta la entrega.");
      setCart({}); setOpen(false); refresh(); navigate("/pedidos");
    } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };

  return (
    <div className="space-y-6">
      <div className="neon-card overflow-hidden relative fade-up">
        <img src="https://images.pexels.com/photos/19036022/pexels-photo-19036022.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940" alt="Comida" className="absolute inset-0 h-full w-full object-cover opacity-30" />
        <div className="absolute inset-0 bg-gradient-to-r from-background via-background/80 to-transparent" />
        <div className="relative p-6 sm:p-8">
          <p className="text-xs uppercase tracking-widest text-cyan-300 font-semibold">Hola, {user?.name?.split(" ")[0]}</p>
          <h1 className="font-display text-3xl sm:text-4xl lg:text-5xl font-extrabold tracking-tight mt-1">¿Qué comemos hoy?</h1>
          <p className="text-muted-foreground mt-2 text-sm sm:text-base">Paga con tu saldo USDT o CUP. Elige tu mensajero. Horario de pedidos: {settings.order_hours_start}–{settings.order_hours_end}.</p>
          {!inHours && <p className="mt-2 inline-block rounded-lg bg-rose-500/20 text-rose-200 text-xs px-3 py-1.5" data-testid="out-of-hours-banner">Fuera de horario: los pedidos se reanudan a las {settings.order_hours_start}.</p>}
        </div>
      </div>

      <div className="flex gap-2 overflow-x-auto scrollbar-none -mx-4 px-4">
        {cats.map((c) => (
          <button key={c} data-testid={`food-cat-${c}`} onClick={() => setCat(c)} className={`shrink-0 rounded-full px-4 py-2 text-sm font-semibold transition-colors ${cat === c ? "bg-primary text-primary-foreground" : "bg-secondary text-muted-foreground hover:text-foreground"}`}>{c}</button>
        ))}
      </div>

      <Section title="Menú" subtitle={`${shown.length} platos disponibles`}>
        {shown.length === 0 ? <Empty text="No hay comidas disponibles en esta categoría." /> : (
          <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-4">
            {shown.map((it, i) => (
              <article key={it.id} data-testid={`food-card-${it.id}`} className="neon-card overflow-hidden flex flex-col fade-up" style={{ animationDelay: `${i * 50}ms` }}>
                <div className="relative h-40">
                  <img src={fileUrl(it.image_url)} alt={it.name} className="h-full w-full object-cover" />
                  {it.offer_price && <span className="absolute top-3 left-3 rounded-full bg-emerald-500 text-background text-xs font-bold px-2.5 py-1">OFERTA</span>}
                  {(it.stock ?? 1) <= 0 ? <span className="absolute top-3 right-3 rounded-full bg-rose-500 text-white text-xs font-bold px-2.5 py-1" data-testid={`food-soldout-${it.id}`}>AGOTADO</span>
                    : (it.stock ?? 99) <= 5 && <span className="absolute top-3 right-3 rounded-full bg-amber-500 text-background text-xs font-bold px-2.5 py-1">Quedan {it.stock}</span>}
                  <span className="absolute bottom-3 right-3 rounded-full bg-background/80 backdrop-blur text-xs px-2 py-1 flex items-center gap-1"><Clock size={12} /> {it.prep_time} min</span>
                </div>
                <div className="p-4 flex-1 flex flex-col gap-3">
                  <div><h3 className="font-display font-semibold text-lg leading-tight">{it.name}</h3><p className="text-sm text-muted-foreground line-clamp-2">{it.description}</p></div>
                  <div className="mt-auto flex items-end justify-between gap-3">
                    <div>
                      {it.offer_price && <span className="text-xs text-muted-foreground line-through font-mono">{fmtUsdt(it.price)} USDT</span>}
                      <Price usdt={it.offer_price || it.price} />
                    </div>
                    {(it.stock ?? 1) <= 0 ? <span className="text-sm text-muted-foreground">No disponible</span> : cart[it.id] ? (
                      <div className="flex items-center gap-2 bg-secondary rounded-xl p-1">
                        <Button size="icon" variant="ghost" className="h-10 w-10" data-testid={`food-minus-${it.id}`} onClick={() => add(it, -1)}><Minus size={16} /></Button>
                        <span className="font-mono font-bold w-5 text-center" data-testid={`food-qty-${it.id}`}>{cart[it.id].qty}</span>
                        <Button size="icon" variant="ghost" className="h-10 w-10" data-testid={`food-plus-${it.id}`} disabled={cart[it.id].qty >= (it.stock ?? 99)} onClick={() => add(it, 1)}><Plus size={16} /></Button>
                      </div>
                    ) : (
                      <Button data-testid={`food-add-${it.id}`} onClick={() => add(it)} className="tap h-11 bg-primary text-primary-foreground hover:bg-cyan-400"><Plus size={18} className="mr-1" /> Agregar</Button>
                    )}
                  </div>
                </div>
              </article>
            ))}
          </div>
        )}
      </Section>

      {count > 0 && (
        <button data-testid="cart-button" onClick={() => setOpen(true)} className="fixed bottom-24 lg:bottom-8 right-4 lg:right-8 z-40 flex items-center gap-3 rounded-2xl bg-emerald-500 text-background px-5 py-4 font-bold glow-green hover:bg-emerald-400 transition-colors">
          <ShoppingCart /> {count} · {fmtUsdt(total)} USDT
        </button>
      )}

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-md max-h-[92vh] overflow-auto" data-testid="checkout-dialog">
          <DialogHeader><DialogTitle className="font-display text-2xl">Tu pedido</DialogTitle></DialogHeader>
          <div className="space-y-2 max-h-40 overflow-auto">
            {lines.map((l) => (
              <div key={l.item.id} className="flex items-center justify-between text-sm">
                <span>{l.qty}× {l.item.name}</span><span className="font-mono">{fmtUsdt((l.item.offer_price || l.item.price) * l.qty)}</span>
              </div>
            ))}
            <div className="flex justify-between text-sm text-muted-foreground"><span>Subtotal</span><span className="font-mono">{fmtUsdt(subtotal)} USDT</span></div>
          </div>
          <div className="space-y-3">
            <div><Label>Dirección de entrega</Label><Input data-testid="checkout-address-input" className="h-12 mt-1" value={address} onChange={(e) => setAddress(e.target.value)} placeholder="Calle, número, municipio" /></div>
            <div><Label>Notas para la cocina / chofer</Label><Textarea data-testid="checkout-notes-input" className="mt-1" value={notes} onChange={(e) => setNotes(e.target.value)} /></div>
          </div>
          <PaymentOptions currency={currency} setCurrency={setCurrency} total={total} driverId={driverId} setDriverId={pickDriver} point={point} setPoint={setPoint} testId="checkout-pay" />
          <p className="text-xs text-muted-foreground">El dinero queda retenido y se libera al confirmar la entrega.</p>
          <Button data-testid="checkout-pay-button" disabled={busy || address.length < 3 || !canPay(user, currency, total, settings.cup_rate) || !inHours} onClick={checkout} className="w-full tap bg-emerald-500 text-background hover:bg-emerald-400">
            {busy ? <Loader2 className="animate-spin" /> : !inHours ? "Fuera de horario" : !canPay(user, currency, total, settings.cup_rate) ? "Saldo insuficiente" : "Confirmar y pagar"}
          </Button>
        </DialogContent>
      </Dialog>
    </div>
  );
}
